import { makeAutoObservable } from "mobx";

import type { TpSlData, TradeDirection } from "@components/hooks/useTradeboxInputs";

import { Address } from "@utils/Address";
import { BN } from "@utils/BN";
import { formatNumber, formatUsd } from "@utils/Format";

import { tradeService } from "@src/services";

import type { RootStore } from "./RootStore";

interface OpenOrderPayload {
  size: BN;
  price: BN;
  side: "buy" | "sell";
  type: "limit" | "market";
  timeInForce?: "GTC" | "IOC" | "FOK" | "PostOnly" | "GTD";
  gtdExpireAtMs?: number;
  reduceOnly?: boolean;
  tpSlData?: TpSlData;
}

interface ClosePositionPayload {
  market: string;
  size: BN;
  price: BN;
  side: "buy" | "sell";
  type: "limit" | "market";
}

/** Live TP/SL preview for TradingView risk/reward overlay (decimal prices, UI scale). */
export interface ChartTpSlPreview {
  entry: number;
  takeProfit: number | null;
  stopLoss: number | null;
  direction: TradeDirection;
  gainPercent?: number;
  lossPercent?: number;
  baseSize?: number;
}

export class TradeboxStore {
  requiredPrice: number | null = null;

  /** When set, the pro chart draws long/short risk-reward from tradebox TP/SL inputs. */
  chartTpSlPreview: ChartTpSlPreview | null = null;

  constructor(private readonly rootStore: RootStore) {
    makeAutoObservable(this);
  }

  setRequiredPrice = (price: number) => {
    this.requiredPrice = price;
  };

  setChartTpSlPreview = (preview: ChartTpSlPreview | null) => {
    this.chartTpSlPreview = preview;
  };

  openOrder = async ({
    size,
    price,
    side,
    type,
    timeInForce = "GTC",
    gtdExpireAtMs,
    reduceOnly = false,
    tpSlData,
  }: OpenOrderPayload) => {
    if (this.guardGeoRestricted()) {
      return;
    }

    const { marketStore, toastStore, accountStore, balanceStore, myOrdersStore } = this.rootStore;

    if (!marketStore.activeMarket) {
      return;
    }

    const activeTradingAccount = accountStore.activeTradingAccount;
    if (!activeTradingAccount || !activeTradingAccount.privateKey) {
      return;
    }

    const symbol = marketStore.activeMarket.symbol;

    const result = await tradeService.openOrder({
      tradingAccount: activeTradingAccount,
      symbol,
      size,
      price,
      side,
      type,
      timeInForce,
      gtdExpireAtMs,
      reduceOnly,
      tpSlData,
    });

    if (!result.success) {
      toastStore.show({
        title: "Order Failed",
        content: result.error ?? "Failed to open order. Please try again.",
        type: "error",
      });
      return;
    }

    // Keep portfolio metrics (equity/margin) in sync right after trade actions.
    void balanceStore.fetchAccountBalances();
    if (marketStore.activeMarket?.addr) {
      void myOrdersStore.loadUserOrdersForMarket(marketStore.activeMarket.addr);
    }
  };

  closeOrder = async (orderId: string) => {
    await this.closeOrders([orderId]);
  };

  closeOrders = async (orderIds: string[]) => {
    if (this.guardGeoRestricted()) {
      return;
    }

    const { marketStore, toastStore, accountStore, myOrdersStore, balanceStore } = this.rootStore;

    const activeTradingAccount = accountStore.activeTradingAccount;
    if (!activeTradingAccount) {
      return;
    }

    const ordersBySymbol = new Map<string, string[]>();
    const marketAddrsToRefresh = new Set<string>();
    for (const orderId of orderIds) {
      const order = myOrdersStore.openOrders.find((o) => o.sid === orderId);
      if (!order) continue;

      marketAddrsToRefresh.add(order.market_addr);

      const market = marketStore.allMarkets.find((m) => m.addr === order.market_addr);
      if (!market) continue;

      const symbol = market.symbol;
      if (!ordersBySymbol.has(symbol)) {
        ordersBySymbol.set(symbol, []);
      }
      ordersBySymbol.get(symbol)!.push(orderId);
    }

    let allSuccess = true;
    let lastError: string | undefined;

    for (const [symbol, ids] of ordersBySymbol) {
      const result = await tradeService.closeOrders({
        subAccountAddress: activeTradingAccount.address,
        symbol,
        orderIds: ids,
      });

      if (!result.success) {
        allSuccess = false;
        lastError = result.error;
      }
    }

    if (!allSuccess) {
      toastStore.show({
        title: "Order Cancel Failed",
        content: lastError ?? "Failed to cancel orders. Please try again.",
        type: "error",
      });
      return;
    }

    toastStore.show({
      title: "Orders Cancelled",
      content: `Cancelled ${orderIds.length} orders successfully`,
      type: "success",
    });

    void balanceStore.fetchAccountBalances();
    for (const addr of marketAddrsToRefresh) {
      void myOrdersStore.loadUserOrdersForMarket(addr);
    }
  };

  closeAllOrders = async () => {
    const { myOrdersStore } = this.rootStore;
    const allOrderIds = myOrdersStore.openOrders.map((order) => order.sid);
    await this.closeOrders(allOrderIds);
  };

  closePosition = async (position: ClosePositionPayload) => {
    await this.closePositions([position]);
  };

  closePositions = async (positions: ClosePositionPayload[]) => {
    if (this.guardGeoRestricted()) {
      return;
    }

    const { toastStore, accountStore, balanceStore, marketStore, myOrdersStore } = this.rootStore;

    const activeTradingAccount = accountStore.activeTradingAccount;
    if (!activeTradingAccount || !activeTradingAccount.privateKey) {
      return;
    }

    const positionsWithSymbol = this.mapPositionsToParams(positions);

    const result = await tradeService.closePositions({
      positions: positionsWithSymbol,
    });

    if (!result.success) {
      toastStore.show({
        title: "Position Close Failed",
        content: result.error ?? "Failed to close positions. Please try again.",
        type: "error",
      });
      return;
    }

    toastStore.show({
      title: "Positions Closed",
      content: `Closed ${positions.length} positions successfully`,
      type: "success",
    });

    void balanceStore.fetchAccountBalances();
    if (marketStore.activeMarket?.addr) {
      void myOrdersStore.loadUserOrdersForMarket(marketStore.activeMarket.addr);
    }
  };

  private static readonly LEVERAGE_WS_TIMEOUT_MS = 15_000;

  setMarketLeverage = async (leverage: number) => {
    if (this.guardGeoRestricted()) {
      return;
    }

    const { marketStore, toastStore, accountStore, myOrdersStore } = this.rootStore;

    if (!marketStore.activeMarket) {
      return;
    }

    const activeTradingAccount = accountStore.activeTradingAccount;
    if (!activeTradingAccount) {
      return;
    }

    const marketAddr = Address.from(marketStore.activeMarket.addr).toString();
    const subAccountAddr = Address.from(activeTradingAccount.address).toString();
    const hasPosition = myOrdersStore.openPosition.some((p) => p.market_addr === marketAddr);
    const hasOrders = myOrdersStore.openOrders.some((o) => o.market_addr === marketAddr);
    const needsWsConfirmation = hasPosition || hasOrders;

    if (needsWsConfirmation) {
      marketStore.addPendingLeverageChange(marketAddr, leverage);
    }

    try {
      await tradeService.setLeverage({
        subAccountAddress: activeTradingAccount.address,
        symbol: marketStore.activeMarket.symbol,
        leverage,
      });

      if (needsWsConfirmation) {
        marketStore.activatePendingLeverageChange(marketAddr);

        toastStore.show({
          title: "Leverage Change Requested",
          content: `Requesting leverage change to ${leverage}x`,
          type: "info",
        });

        const freshPos = myOrdersStore.openPosition.find((p) => p.market_addr === marketAddr);
        if (freshPos?.leverage === leverage) {
          marketStore.cancelPendingLeverageChange(marketAddr);
          marketStore.setLeverages([{ market_addr: marketAddr, leverage, sub_account_address: subAccountAddr }]);
          toastStore.show({
            title: "Leverage Updated",
            content: `Leverage set to ${leverage}x`,
            type: "success",
          });
        } else {
          setTimeout(() => {
            const pending = marketStore.consumePendingLeverageChange(marketAddr);
            if (pending !== undefined) {
              toastStore.show({
                title: "Leverage Set Failed",
                content: "Failed to set leverage. Please try again.",
                type: "error",
              });
            }
          }, TradeboxStore.LEVERAGE_WS_TIMEOUT_MS);
        }
      } else {
        marketStore.setLeverages([{ market_addr: marketAddr, leverage, sub_account_address: subAccountAddr }]);
        toastStore.show({
          title: "Leverage Updated",
          content: `Leverage set to ${leverage}x`,
          type: "success",
        });
      }
    } catch (_error) {
      marketStore.cancelPendingLeverageChange(marketAddr);
      toastStore.show({
        title: "Leverage Set Failed",
        content: "Failed to set leverage. Please try again.",
        type: "error",
      });
    }
  };

  private buildOrderSuccessMessage = (type: "limit" | "market", side: "buy" | "sell", price: BN, size: BN) => {
    const { marketStore } = this.rootStore;
    const typeFormatted = type.charAt(0).toUpperCase() + type.slice(1);
    const directionLabel = side === "buy" ? "Long" : "Short";
    const asset = marketStore.activeMarket!.symbol.toUpperCase();

    const title = `${typeFormatted} ${directionLabel} ${asset} Opened`;
    const content = `Opened at ${formatUsd(
      BN.formatUnits(price, marketStore.activeMarket!.quote_decimals),
    )} with ${marketStore.currentLeverage}x leverage · Size ${formatNumber(
      BN.formatUnits(size, marketStore.activeMarket!.base_decimals),
    )}`;

    return { title, content };
  };

  private getMarketSymbol = (market: string): string => {
    const { marketStore } = this.rootStore;
    const marketItem = marketStore.allMarkets.find((m) => m.symbol.toLowerCase() === market.toLowerCase());
    return marketItem ? marketItem.symbol : "";
  };

  private mapPositionsToParams = (positions: ClosePositionPayload[]) => {
    const { accountStore } = this.rootStore;
    const subAccountAddress = accountStore.activeTradingAccount?.address ?? "";

    return positions.map((pos) => {
      const symbol = this.getMarketSymbol(pos.market);

      return {
        subAccountAddress,
        symbol,
        size: pos.size,
        price: pos.price,
        side: pos.side,
        type: pos.type,
      };
    });
  };

  reset = () => {
    this.requiredPrice = null;
    this.chartTpSlPreview = null;
  };

  private guardGeoRestricted = () => {
    if (!this.rootStore.geoFenceStore.isRestricted) {
      return false;
    }

    this.rootStore.toastStore.show({
      type: "warning",
      title: "Limited Functionality",
      content: "Unavailable in restricted jurisdiction.",
    });
    return true;
  };
}
